import React, { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ChevronDown } from 'lucide-react';
import axios from 'axios';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Archive = () => {
  const { language, t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedPoster, setSelectedPoster] = useState(null);

  // Archived event posters with month/year
  const archivedPosters = [
    {
      id: 'archive-1',
      image: 'https://customer-assets.emergentagent.com/job_kultur-brucke/artifacts/0mzxgf1n_plakat%20Kopie%20Kopie.jpg',
      title_de: 'Buchpräsentation: La fabbrica dei fuochi',
      title_it: 'Presentazione del libro: La fabbrica dei fuochi',
      month_de: 'Mai',
      month_it: 'Maggio',
      year: 2025,
      location: 'Ristorante Rosa Bianca, Karlsruhe',
    },
    {
      id: 'archive-2',
      image: 'https://customer-assets.emergentagent.com/job_kultur-brucke/artifacts/2kr0f5b4_Plakat%20OK.jpg',
      title_de: 'Cinemavisione: Nostalgia',
      title_it: 'Cinemavisione: Nostalgia',
      month_de: 'Mai',
      month_it: 'Maggio',
      year: 2026,
      location: 'Kinemathek Karlsruhe',
    },
  ];

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await axios.get(`${API}/archive/years`);
        const currentYr = new Date().getFullYear();
        const fb = [];
        for (let y = currentYr; y >= 2011; y--) fb.push(y);
        setYears(Array.isArray(response.data) ? response.data : fb);
      } catch (error) {
        console.error('Error fetching years:', error);
        // Fallback years
        const currentYear = new Date().getFullYear();
        const fallbackYears = [];
        for (let y = currentYear; y >= 2011; y--) {
          fallbackYears.push(y);
        }
        setYears(fallbackYears);
      }
    };
    fetchYears();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        let response;
        if (selectedYear === 'all') {
          response = await axios.get(`${API}/events`);
        } else {
          response = await axios.get(`${API}/events/year/${selectedYear}`);
        }
        setEvents(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [selectedYear]);

  // Group events by year for timeline display
  const eventsByYear = events.reduce((acc, event) => {
    const year = event.year;
    if (!acc[year]) {
      acc[year] = [];
    }
    acc[year].push(event);
    return acc;
  }, {});

  const sortedYears = Object.keys(eventsByYear).sort((a, b) => b - a);

  return (
    <div className="overflow-hidden" data-testid="archive-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6" data-testid="archive-title">
              {t('archive_title')}
            </h1>
            <p className="text-lg text-foreground/70 max-w-2xl mb-8">
              {t('archive_subtitle')}
            </p>

            {/* Year Filter */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-foreground/60">{t('archive_select_year')}:</span>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[180px] bg-white" data-testid="year-selector">
                  <SelectValue placeholder={t('archive_select_year')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('archive_all_years')}</SelectItem>
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          
          {/* Archived Posters Section */}
          {archivedPosters.length > 0 && (
            <div className="mb-20">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-12 text-center">
                {language === 'de' ? 'Vergangene Veranstaltungen' : 'Eventi passati'}
              </h2>
              
              {/* Group by Year and Month */}
              {[2026, 2025].map((year) => {
                const postersForYear = archivedPosters.filter(p => p.year === year);
                if (postersForYear.length === 0) return null;
                
                return (
                  <div key={year} className="mb-12">
                    <div className="flex items-center justify-center mb-8">
                      <span className="year-badge">{year}</span>
                    </div>
                    
                    {/* Group by month within year */}
                    {['Mai', 'April', 'März', 'Februar', 'Januar'].map((monthDe) => {
                      const postersForMonth = postersForYear.filter(p => p.month_de === monthDe);
                      if (postersForMonth.length === 0) return null;
                      
                      const monthIt = postersForMonth[0]?.month_it;
                      
                      return (
                        <div key={monthDe} className="mb-8">
                          <h3 className="font-heading text-xl font-semibold mb-6 text-center text-foreground/70">
                            {language === 'de' ? monthDe : monthIt} {year}
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                            {postersForMonth.map((poster, index) => (
                              <motion.div
                                key={poster.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="cursor-pointer group"
                                onClick={() => setSelectedPoster(poster)}
                                data-testid={`archived-poster-${poster.id}`}
                              >
                                <div className="relative overflow-hidden luxury-shadow">
                                  <img
                                    src={poster.image}
                                    alt={language === 'de' ? poster.title_de : poster.title_it}
                                    className="w-full aspect-[3/4] object-cover transition-transform duration-500 group-hover:scale-105"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                    <p className="font-semibold text-sm">{language === 'de' ? poster.title_de : poster.title_it}</p>
                                    <p className="text-xs text-white/80">{poster.location}</p>
                                  </div>
                                </div>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          {/* Divider */}
          <div className="gradient-line mb-16" />
          
          <h2 className="font-heading text-2xl md:text-3xl font-semibold mb-12 text-center">
            {language === 'de' ? 'Weitere Events aus dem Archiv' : 'Altri eventi dall\'archivio'}
          </h2>

          {loading ? (
            <div className="space-y-12">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-8 bg-gray-200 w-24 mb-6" />
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2].map((j) => (
                      <div key={j}>
                        <div className="aspect-video bg-gray-200 mb-4" />
                        <div className="h-4 bg-gray-200 w-3/4 mb-2" />
                        <div className="h-4 bg-gray-200 w-1/2" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-foreground/20" />
              <p className="text-foreground/60 text-lg">{t('archive_no_events')}</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-ferrari via-mercedes-silver to-mediterranean hidden md:block" 
                style={{ background: 'linear-gradient(to bottom, hsl(0 100% 42%), hsl(0 0% 75%), hsl(208 100% 27%))' }} />

              {sortedYears.map((year, yearIndex) => (
                <motion.div
                  key={year}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: yearIndex * 0.1 }}
                  className="mb-16 last:mb-0"
                >
                  {/* Year Header */}
                  <div className="relative flex items-center justify-center mb-8">
                    <div className="year-badge z-10" data-testid={`year-badge-${year}`}>
                      {year}
                    </div>
                  </div>

                  {/* Events Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {eventsByYear[year].map((event, eventIndex) => (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: eventIndex * 0.1 }}
                        className="event-card bg-white border border-black/5 overflow-hidden"
                        data-testid={`archive-event-${year}-${eventIndex}`}
                      >
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={event.image_url || '/placeholder-event.svg'}
                            alt={language === 'de' ? event.title_de : event.title_it}
                            className="card-image w-full h-full object-cover transition-transform duration-500"
                          />
                        </div>
                        <div className="p-6">
                          <div className="flex items-center gap-4 text-sm text-foreground/60 mb-3">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {new Date(event.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'it-IT', {
                                day: 'numeric',
                                month: 'long',
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin size={14} />
                              {event.location}
                            </span>
                          </div>
                          <h3 className="font-heading text-lg font-semibold mb-2">
                            {language === 'de' ? event.title_de : event.title_it}
                          </h3>
                          <p className="text-sm text-foreground/60 line-clamp-2">
                            {language === 'de' ? event.description_de : event.description_it}
                          </p>
                        </div>
                        <div className="card-border h-1 bg-gray-200 transition-colors duration-300" />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Founding Story Section */}
      <section className="py-20 bg-[#1A1A1A] text-white" data-testid="founding-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="year-badge mb-6 inline-block">2011</span>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6">
                {language === 'de' ? 'Der Anfang einer Freundschaft' : 'L\'inizio di un\'amicizia'}
              </h2>
              <p className="text-white/70 leading-relaxed mb-4">
                {language === 'de'
                  ? 'Im Jahr 2011 wurde Amitalia von einer Gruppe deutsch-italienischer Enthusiasten in Bruchsal gegründet. Was als kleine Initiative begann, ist heute ein lebendiger Verein.'
                  : 'Nel 2011, Amitalia è stata fondata a Bruchsal da un gruppo di appassionati italo-tedeschi. Quello che è iniziato come una piccola iniziativa è oggi un\'associazione vivace.'}
              </p>
              <p className="text-white/70 leading-relaxed">
                {language === 'de'
                  ? 'Seitdem organisieren wir regelmäßig Veranstaltungen: von Sprachkursen über Kulturabende bis hin zu Weinproben und Konzerten.'
                  : 'Da allora, organizziamo regolarmente eventi: dai corsi di lingua alle serate culturali, dalle degustazioni di vino ai concerti.'}
              </p>
            </div>
            <div className="relative">
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-german-gold/20 via-white to-ferrari/20 luxury-shadow flex items-center justify-center">
                <span className="font-heading text-6xl font-semibold text-foreground/10">2011</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Poster Modal */}
      {selectedPoster && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setSelectedPoster(null)}
          data-testid="archive-poster-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative max-w-4xl w-full max-h-[95vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedPoster.image}
              alt={language === 'de' ? selectedPoster.title_de : selectedPoster.title_it}
              className="w-full h-auto max-h-[90vh] object-contain"
            />
            <button
              onClick={() => setSelectedPoster(null)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-foreground p-2 rounded-full transition-colors"
              data-testid="close-archive-poster-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Archive;
